import { Component, EventEmitter, HostListener, Input, ViewEncapsulation } from '@angular/core';


@Component({
	selector: 'portal-modal',
	template: `
		<div class="pm-overlay" *ngIf="isOpen" (click)="backdropClick()">
			<div class="pm-modal" [ngClass]="'pm-' + size" (click)="$event.stopPropagation()">
				<div class="pm-header" *ngIf="header">
					<div class="pm-header-text">
						<div class="pm-title">{{ modalTitle }}</div>
						<div class="pm-subtitle" *ngIf="subtitle">{{ subtitle }}</div>
					</div>
					<button type="button" class="pm-close" *ngIf="showCloseButton" (click)="close()" aria-label="Close"><i class="wq wq-close"></i></button>
				</div>
				<div class="pm-body"><ng-content></ng-content></div>
				<div class="pm-footer" *ngIf="footer"><ng-content select="[portal-modal-footer]"></ng-content></div>
			</div>
		</div>
	`,
	encapsulation: ViewEncapsulation.None,
	styles: [`
		.pm-overlay {
			position: fixed;
			inset: 0;
			z-index: 900;
			background: rgba(16, 18, 24, 0.55);
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 1rem;
		}

		.pm-modal {
			background: var(--c-surface);
			color: var(--c-text-1);
			border-radius: 0.6rem;
			width: 34rem;
			max-width: 100%;
			max-height: 90vh;
			display: flex;
			flex-direction: column;
			overflow: hidden;
			box-shadow: 0 18px 48px rgba(0, 0, 0, 0.35);
		}

		.pm-modal.pm-sm { width: 26rem; }
		.pm-modal.pm-lg { width: 46rem; }

		/* For content that is genuinely tabular — the change-order breakdown carries up
		   to seven columns and needs the room. */
		.pm-modal.pm-xl { width: 68rem; }

		.pm-header {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			gap: 1rem;
			padding: 1.25rem 1.4rem 0.75rem;
		}

		.pm-title {
			font-size: 1.35rem;
			font-weight: 700;
			color: var(--c-text-1);
		}

		.pm-subtitle {
			margin-top: 0.35rem;
			font-size: 0.85rem;
			line-height: 1.4;
			color: var(--c-text-muted);
		}

		.pm-close {
			flex-shrink: 0;
			background: none;
			border: none;
			cursor: pointer;
			color: var(--c-text-muted);
			font-size: 1rem;
			padding: 0.5rem;
			border-radius: 0.2rem;
			line-height: 1;
		}

		.pm-close:hover {
			color: var(--c-text-1);
			background: var(--c-hover);
		}

		.pm-body {
			padding: 0.5rem 1.4rem 1rem;
			overflow-y: auto;
			/* Keep scrolling inside the modal, not the page behind it. */
			overscroll-behavior: contain;
			-webkit-overflow-scrolling: touch;
		}

		.pm-footer {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			gap: 0.6rem;
			padding: 1rem 1.4rem 1.25rem;
			border-top: 1px solid var(--c-border-1);
		}

		/* Mobile: loaded in a browser (not an app), so size to the visible viewport
		   and keep the modal clear of the browser URL bar / home indicator. */
		@media (max-width: 768px) {
			.pm-overlay {
				padding: 1rem;
				padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
			}

			.pm-modal {
				max-height: 90dvh;
			}
		}
	`]
})
export class PortalModalComponent {

	@Input() modalTitle = '';
	@Input() subtitle = '';
	@Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
	@Input() header = true;
	@Input() footer = true;
	@Input() showCloseButton = true;
	@Input() easyClose = true;

	isOpen = false;
	// Reassigned by PortalModalBaseComponent to the subclass's own `close` output,
	// so parents listen via (close) — same wiring trick as ui-modal.
	onClose = new EventEmitter<any>();

	// The page behind is frozen while this is open by the .pm-overlay scroll lock in
	// styles.scss — nothing to do here.
	open(): void {
		this.isOpen = true;
	}

	close(data: any = null): void {
		if (!this.isOpen) return;
		this.isOpen = false;
		this.onClose.emit(data);
	}

	backdropClick(): void {
		if (this.easyClose) this.close();
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.isOpen && this.easyClose) this.close();
	}
}
